import { useState } from "react";
import { Sparkles, PenLine, Link2, Scale, Copy, Plus, Check, X } from "lucide-react";
import { authFetch } from "@/adminAuth";

const API = `/api`;

const input =
  "w-full bg-transparent border border-white/15 focus:border-gold outline-none px-4 py-3 text-sm text-[#FAF8F5] placeholder:text-[#626773] transition-colors";

const INSTRUCTIONS = [
  { value: "ameliorer", label: "Améliorer le style" },
  { value: "simplifier", label: "Simplifier (non-juriste)" },
  { value: "developper", label: "Développer / enrichir" },
  { value: "corriger", label: "Corriger orthographe & typo" },
];

const STATUTS = {
  a_jour: { label: "À jour", cls: "text-emerald-400 border-emerald-400/30" },
  a_verifier: { label: "À vérifier", cls: "text-amber-400 border-amber-400/30" },
  obsolete: { label: "Obsolète", cls: "text-red-400 border-red-400/30" },
};

export default function AiAssistant({ fiche, setFiche, type, slug, allFiches }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [passage, setPassage] = useState("");
  const [instr, setInstr] = useState("ameliorer");
  const [redacResult, setRedacResult] = useState("");
  const [copied, setCopied] = useState(false);
  const [links, setLinks] = useState(null);
  const [refs, setRefs] = useState(null);

  const call = async (action, extra = {}) => {
    if (busy) return;
    setBusy(action);
    setError("");
    try {
      const resp = await authFetch(`${API}/admin/knowledge/${type}/${slug}/ai-assist`, {
        method: "POST",
        body: JSON.stringify({ action, ...extra }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(typeof data.detail === "string" ? data.detail : "L'assistant a échoué — réessayez.");
        return null;
      }
      return data.result;
    } catch {
      setError("L'assistant a échoué — réessayez.");
      return null;
    } finally {
      setBusy("");
    }
  };

  const runRedaction = async () => {
    const out = await call("redaction", { text: passage, instruction: instr });
    if (out) setRedacResult(out);
  };

  const runExcerpt = async () => {
    const out = await call("excerpt");
    if (out) setFiche((f) => ({ ...f, excerpt: out }));
  };

  const runLinks = async () => {
    const out = await call("suggest_links");
    if (out) setLinks(out);
  };

  const addLink = (code) => {
    const [t, s] = code.split("/");
    setFiche((f) => {
      const cur = f.related_slugs || [];
      const has = cur.some((p) =>
        Array.isArray(p) ? p[0] === t && p[1] === s : p === s
      );
      return has ? f : { ...f, related_slugs: [...cur, [t, s]] };
    });
    setLinks((ls) => ls.filter((c) => c !== code));
  };

  const runRefs = async () => {
    const out = await call("check_law_refs");
    if (out) setRefs(out);
  };

  const copyResult = async () => {
    try {
      await navigator.clipboard.writeText(redacResult);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const titleOf = (code) => {
    const [t, s] = code.split("/");
    const f = (allFiches || []).find((x) => x.type === t && x.slug === s);
    return f ? `${f.title} (${t})` : code;
  };

  const btnCls =
    "inline-flex items-center gap-2 border border-gold/40 text-gold text-xs uppercase tracking-[0.2em] px-5 py-3 hover:bg-gold/10 transition-colors disabled:opacity-50";

  return (
    <div className="border border-gold/25 bg-ink-card p-6" data-testid="ai-assistant">
      <div className="flex items-center gap-3 flex-wrap">
        <Sparkles className="w-4 h-4 text-gold" />
        <span className="label-accent">Assistant IA</span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold/60 border border-gold/20 px-2.5 py-1">
          L'IA propose — l'avocat valide
        </span>
      </div>

      {error && (
        <p className="mt-4 text-xs text-red-400" data-testid="ai-error">{error}</p>
      )}

      {/* 1. Aide à la rédaction */}
      <div className="mt-6" data-testid="ai-redaction-block">
        <label className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] mb-3">
          <PenLine className="w-3.5 h-3.5 text-gold/70" /> Aide à la rédaction
        </label>
        <textarea
          rows={3}
          value={passage}
          onChange={(e) => setPassage(e.target.value)}
          placeholder="Collez ici le passage à retravailler (ou rédigez un début)…"
          className={input + " min-h-[70px]"}
          data-testid="ai-passage-input"
        />
        <div className="mt-3 flex flex-col sm:flex-row gap-3">
          <select
            value={instr}
            onChange={(e) => setInstr(e.target.value)}
            className={input + " bg-ink cursor-pointer sm:w-64"}
            data-testid="ai-instruction-select"
          >
            {INSTRUCTIONS.map((i) => (
              <option key={i.value} value={i.value} className="bg-ink">{i.label}</option>
            ))}
          </select>
          <button onClick={runRedaction} disabled={!!busy || passage.trim().length < 3} className={btnCls} data-testid="ai-redaction-run">
            {busy === "redaction" ? "Rédaction…" : "Retravailler"}
          </button>
          <button onClick={runExcerpt} disabled={!!busy} className={btnCls} data-testid="ai-excerpt-run">
            {busy === "excerpt" ? "Rédaction…" : "Rédiger l'extrait"}
          </button>
        </div>
        {redacResult && (
          <div className="mt-4 border border-white/10 p-4" data-testid="ai-redaction-result">
            <p className="text-sm text-[#FAF8F5] leading-relaxed whitespace-pre-wrap">{redacResult}</p>
            <div className="mt-3 flex gap-3">
              <button
                onClick={copyResult}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-gold hover:text-gold-light transition-colors"
                data-testid="ai-redaction-copy"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copié" : "Copier"}
              </button>
              <button
                onClick={() => setRedacResult("")}
                className="inline-flex items-center gap-1 text-xs text-[#626773] hover:text-red-400 transition-colors"
                data-testid="ai-redaction-dismiss"
              >
                <X className="w-3.5 h-3.5" /> Effacer
              </button>
            </div>
          </div>
        )}
        <p className="mt-2 text-xs text-[#626773]">
          « Rédiger l'extrait » remplit directement le champ Extrait ci-dessus — pensez à enregistrer.
        </p>
      </div>

      {/* 2. Suggestions de liens */}
      <div className="mt-8 pt-6 border-t border-white/10" data-testid="ai-links-block">
        <label className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] mb-3">
          <Link2 className="w-3.5 h-3.5 text-gold/70" /> Sujets liés suggérés
        </label>
        <button onClick={runLinks} disabled={!!busy} className={btnCls} data-testid="ai-links-run">
          {busy === "suggest_links" ? "Analyse du catalogue…" : "Suggérer des liens"}
        </button>
        {links !== null && (
          <div className="mt-4" data-testid="ai-links-results">
            {links.length === 0 ? (
              <p className="text-xs text-[#626773] italic">Aucune nouvelle fiche vraiment liée trouvée dans le catalogue.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {links.map((code) => (
                  <span key={code} className="inline-flex items-center gap-2 text-xs text-gold/80 border border-gold/25 px-3 py-1.5" data-testid={`ai-link-${code.replace("/", "-")}`}>
                    <a
                      href={`/admin/fiches/${code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-gold hover:underline underline-offset-2 transition-colors"
                      data-testid={`ai-link-view-${code.replace("/", "-")}`}
                    >
                      {titleOf(code)}
                    </a>
                    <button onClick={() => addLink(code)} className="hover:text-gold" aria-label="Ajouter" data-testid={`ai-link-add-${code.replace("/", "-")}`}>
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-[#626773]">
              Le + ajoute la fiche aux « Sujets liés » ci-dessous — enregistrez pour confirmer.
            </p>
          </div>
        )}
      </div>

      {/* 3. Références de lois */}
      <div className="mt-8 pt-6 border-t border-white/10" data-testid="ai-refs-block">
        <label className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] mb-3">
          <Scale className="w-3.5 h-3.5 text-gold/70" /> Références légales
        </label>
        <button onClick={runRefs} disabled={!!busy} className={btnCls} data-testid="ai-refs-run">
          {busy === "check_law_refs" ? "Analyse en cours…" : "Vérifier les références"}
        </button>
        {refs !== null && (
          <div className="mt-4" data-testid="ai-refs-results">
            {refs.length === 0 ? (
              <p className="text-xs text-[#626773] italic">Aucune référence légale citée dans cette fiche.</p>
            ) : (
              <ul className="space-y-3">
                {refs.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" data-testid={`ai-ref-${i}`}>
                    <span className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.15em] border px-2 py-1 mt-0.5 ${STATUTS[r.statut]?.cls || STATUTS.a_verifier.cls}`}>
                      {STATUTS[r.statut]?.label || r.statut}
                    </span>
                    <span className="text-[#A0A5B1] leading-relaxed">
                      <span className="text-[#FAF8F5]">{r.reference}</span>
                      {r.note && <span className="block text-xs text-[#626773] mt-0.5">{r.note}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-3 text-xs text-[#626773] leading-relaxed">
              Analyse indicative basée sur les connaissances du modèle — vérifiez toujours le texte
              officiel (eJustice) avant de modifier la fiche.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
